import { SubmitButton } from '@website/components/forms/fields/SubmitButton';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import { useMutation } from '@apollo/client';
import { ErrorMessage } from '@hookform/error-message';
import BootstrapTypeahead from '@website/components/forms/fields/BootstrapTypeahead';
import GoogleMapsAutocompleteField from '@website/components/forms/fields/GoogleMapsAutocompleteField';
import MediaSelectorField from '@website/components/forms/fields/MediaSelectorField';
import { AlertDismissible } from '@website/components/utilities/AlertDismissible';
import ErrorPage from '@website/components/utilities/ErrorPage';
import config from '@website/config';
import difficultyLevels from '@website/dataObjects/difficultyLevels';
import {
  Status,
  TripActivityType,
  TripDetailsForTripEditFormQuery,
  UpdateTripDetailsDocument,
  UpdateTripInput,
} from '@website/types/graphqlOperations';
import axios from 'axios';
import Router, { useRouter } from 'next/router';
import { Alert, Col, Form, Row } from 'react-bootstrap';
import generateSlug from './utils/generateSlug';
import useSelectedDifficultLevelDetails from './utils/useSelectedDifficultLevelDetails';

export type TripEditFormType = Omit<
  UpdateTripInput,
  'hostID' | 'image' | 'activities'
> & {
  image: TripDetailsForTripEditFormQuery['tripByURL']['image'];
  activities?: string[];
};

/**
 * tripActivityEnumToLabelString
 *
 * Takes an uppercase underscore separated enum and converts to a string
 * separated by space and in title case
 *
 */
export function tripActivityEnumToLabelString(activityEnum: TripActivityType) {
  return activityEnum
    .toString()
    .toLowerCase()
    .split('_')
    .map((v) => v.charAt(0).toUpperCase() + v.slice(1))
    .join(' ');
}

/**
 * tripActivityLabelStringToEnum
 *
 * Takes a space separated, title case string converts it to uppercase, underscore
 * separated enum similar to the `TripActivityType` enum.
 */
export function tripActivityLabelStringToEnum(activity: string) {
  return activity.toUpperCase().split(' ').join('_') as TripActivityType;
}

export function composeEditTripFormInitialValues(
  trip?: TripDetailsForTripEditFormQuery['tripByURL'],
): TripEditFormType {
  const lat =
    typeof trip?.address?.coordinates?.lat === 'string'
      ? parseFloat(trip?.address?.coordinates.lat)
      : trip?.address?.coordinates?.lat;
  const lng =
    typeof trip?.address?.coordinates?.lng === 'string'
      ? parseFloat(trip?.address?.coordinates.lng)
      : trip?.address?.coordinates?.lng;

  return {
    title: trip?.title,
    urlSlug: trip?.urlSlug,
    description: trip?.description,
    image: trip?.image,
    active: trip?.active === Status.ACTIVE,
    startTimezone: trip?.startTimezone,
    endTimezone: trip?.endTimezone,
    difficultyLevel: trip?.difficultyLevel,
    activities: trip?.activities?.map((activity) =>
      tripActivityEnumToLabelString(activity),
    ),
    tripVideoURL: trip?.tripVideoURL,
    tripVideoURLThumbnail: trip?.tripVideoURLThumbnail,
    includes: trip?.includes,
    excludes: trip?.excludes,
    notice: trip?.notice,
    featuredImages: trip?.featuredImages ?? (trip?.image ? [trip?.image] : []),
    meals: trip?.meals,
    allowPrivateGroups: trip?.allowPrivateGroups,
    address: {
      ...trip?.address,
      addressLine1: trip?.address.addressLine1 ?? '',
      coordinates: {
        lng,
        lat,
      },
    },
  };
}

export function composeTripAPIObject(
  formData: TripEditFormType,
  hostID: string,
  tripID?: string,
): UpdateTripInput {
  const urlSlug = formData.urlSlug || generateSlug(formData.title);
  return {
    ...formData,
    image: {
      // if want to use featured image as main image, we are hiding the image
      // field in the form and using the first featured image as the main image
      // for the trip if it exists, eventually we will remove the image field
      // from the form and use only featured images
      id: formData.featuredImages?.[0]?.id || formData.image?.id,
    },
    urlSlug,
    hostID,
    activities: formData.activities?.map((a) =>
      tripActivityLabelStringToEnum(a),
    ),
    tripVideoURL: formData.tripVideoURL || undefined,
    tripVideoURLThumbnail: formData.tripVideoURLThumbnail
      ? {
          id: formData.tripVideoURLThumbnail.id,
        }
      : undefined,
    featuredImages: formData.featuredImages?.map((image) => ({
      id: image.id,
    })),
    allowPrivateGroups: formData.allowPrivateGroups,
    meals: formData.meals,
    ...(tripID && { id: tripID }),
  };
}

export async function getTimezone(lat: number, lng: number) {
  const timezone = await axios.get(
    `https://maps.googleapis.com/maps/api/timezone/json?location=${lat},${lng}&timestamp=1458000000&key=${config.Google.timezoneApiKey}`,
  );
  return {
    startTimezone: (timezone.data as any).timeZoneId,
    endTimezone: (timezone.data as any).timeZoneId,
  };
}

type Props = {
  trip?: TripDetailsForTripEditFormQuery['tripByURL'];
  hostID: string;
};

/**
 * Main Trip Create/Edit Form Component. routerPush is important for redirecting - particularly when creating a new trip
 * @param {Object} param0 {trip, setReloadTrigger routerPush, ...props}
 */
export default function EditTripForm({ trip, hostID }: Props) {
  const [successMessage, setSuccessMessage] = useState('');
  const [isFormHidden, setIsFormHidden] = useState(false);

  const { query } = useRouter();

  const tripUrl = query?.tripUrl as string;

  const activitiesOptions = useMemo(() => {
    return Object.values(TripActivityType).map((activityEnum) =>
      tripActivityEnumToLabelString(activityEnum),
    );
  }, []);

  const defaultValues = composeEditTripFormInitialValues(trip);

  const {
    clearErrors,
    control,
    formState,
    handleSubmit,
    register,
    setError,
    trigger,
    watch,
    setValue,
  } = useForm<TripEditFormType>({
    mode: 'onBlur',
    defaultValues,
  });

  const generateUrlSlug = useCallback(
    async (title: string) => {
      if (title && !trip?.urlSlug) {
        const urlSlug = generateSlug(title, true);
        setValue('urlSlug', urlSlug);
      }
    },
    [setValue, trip?.urlSlug],
  );

  const difficultyLevelFieldValue = watch(
    'difficultyLevel',
    defaultValues.difficultyLevel,
  );

  const selectedLevel = useSelectedDifficultLevelDetails(
    difficultyLevelFieldValue?.toString(),
  );

  const { errors, isSubmitting } = formState;

  const tripVideoURL = watch('tripVideoURL');

  const [updateTrip, { loading, error, data }] = useMutation(
    UpdateTripDetailsDocument,
  );

  async function submitForm(formData: TripEditFormType) {
    // trigger validation manually, this is to show error messages on our custom
    // controls
    await trigger();

    // this prevents submissions if the form isn't valid
    if (errors && Object.keys(errors)[0]) {
      return;
    }

    const { startTimezone, endTimezone } = await getTimezone(
      formData.address.coordinates.lat,
      formData.address.coordinates.lng,
    );

    const input = composeTripAPIObject(
      {
        ...formData,
        startTimezone,
        endTimezone,
      },
      hostID,
      trip?.id,
    );
    await updateTrip({
      variables: {
        input,
      },
    });
    if (!trip || !trip.id) {
      setIsFormHidden(true);
      setSuccessMessage('Your trip has been created successfully!');
      return;
    }
    setIsFormHidden(false);
    setSuccessMessage('Your trip has been updated successfully!');

    if (formData.urlSlug !== tripUrl) {
      Router.push(`/trips/${formData.urlSlug}/edit`);
    }
  }

  const onDismissAlert = () => {
    // redirect only new trips
    if (!trip || !trip.id) {
      Router.push(`/trips/${data?.updateTrip.urlSlug}`);
    }
  };

  if (error) {
    return <ErrorPage error={error} />;
  }

  return (
    <>
      {successMessage && (
        <Row>
          <Col>
            <AlertDismissible
              onClose={() => onDismissAlert()}
              variant="success"
              title="Trip Edit Form"
              message={successMessage}
            />
          </Col>
        </Row>
      )}
      {!isFormHidden && (
        <Form onSubmit={handleSubmit(submitForm)}>
          <Form.Group controlId="title">
            <Form.Label>Trip title *</Form.Label>
            <Form.Control
              type="text"
              onKeyUp={(e) => {
                generateUrlSlug(e.currentTarget.value);
              }}
              {...register('title', { required: true })}
            />
            <ErrorMessage
              errors={errors}
              name="title"
              render={({ message }) => (
                <Form.Text as="p" className="text-danger">
                  {message ?? 'Title is required!'}
                </Form.Text>
              )}
            />
          </Form.Group>

          <Form.Group controlId="description">
            <Form.Label>Description *</Form.Label>
            <Form.Control
              as="textarea"
              type="text"
              {...register('description', { required: true })}
            />
            <ErrorMessage
              errors={errors}
              name="description"
              render={({ message }) => (
                <Form.Text as="p" className="text-danger">
                  {message ?? 'Description is required!'}
                </Form.Text>
              )}
            />
          </Form.Group>

          <Form.Group controlId="activities">
            <Form.Label>Activities</Form.Label>
            <BootstrapTypeahead
              name="activities"
              placeholder="Select Activities"
              control={control}
              ariaLabel="Activities"
              options={activitiesOptions}
              multiple
            />

            <ErrorMessage
              errors={errors}
              name="activities"
              render={({ message }) => (
                <Form.Text as="p" className="text-danger">
                  {message}
                </Form.Text>
              )}
            />
          </Form.Group>

          <Form.Group controlId="difficulty">
            <Form.Label>Difficulty Level *</Form.Label>
            <Form.Select {...register('difficultyLevel', { required: true })}>
              {difficultyLevels.map(({ value, label }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Form.Select>
            <ErrorMessage
              errors={errors}
              name="difficultyLevel"
              render={({ message }) => (
                <Form.Text as="p" className="text-danger">
                  {message || 'Difficulty Level is Required!'}
                </Form.Text>
              )}
            />
            {selectedLevel && (
              <Alert variant="info" className="mt-2">
                <p>
                  <strong>
                    Please check your trip matches the description below
                  </strong>
                </p>
                <p>{selectedLevel.description}</p>
                <p className="mb-0">Examples:</p>
                <ul>
                  {selectedLevel.examples.map((example) => (
                    <li key={example}>{example}</li>
                  ))}
                </ul>
              </Alert>
            )}
          </Form.Group>

          {/* <Form.Group>
            <Form.Label>Trip Image *</Form.Label>
            <MediaSelectorField
              control={control}
              tripID={trip?.id}
              hostID={hostID}
              required
              name="image"
            />
            <ErrorMessage
              errors={errors}
              name="image"
              render={({ message }) => (
                <Form.Text as="p" className="text-danger">
                  {message ?? 'Trip image is required!'}
                </Form.Text>
              )}
            />
          </Form.Group> */}

          <Form.Group controlId="tripVideoURL">
            <Form.Label>Trip Video</Form.Label>
            <Form.Control
              type="text"
              {...register('tripVideoURL', {
                // allow only youtube or vimeo urls
                pattern: /youtube.com\/watch\?v=|vimeo.com\//,
              })}
            />
            <Form.Text>
              Please enter a YouTube/Vimeo video URL showing highlights for this
              trip.
            </Form.Text>
            <ErrorMessage
              errors={errors}
              name="tripVideoURL"
              render={({ message }) => (
                <Form.Text as="p" className="text-danger">
                  {message ?? 'A valid youtube URL is required!'}
                </Form.Text>
              )}
            />
          </Form.Group>

          {tripVideoURL && (
            <Form.Group controlId="tripVideoURLThumbnail">
              <Form.Label>Trip Video Thumbnail</Form.Label>
              <MediaSelectorField
                control={control}
                tripID={trip?.id}
                hostID={hostID}
                required
                name="tripVideoURLThumbnail"
                selectButtonLabel="Select Trip Video Thumbnail"
              />
              <ErrorMessage
                errors={errors}
                name="tripVideoURL"
                render={({ message }) => (
                  <Form.Text as="p" className="text-danger">
                    {message ?? 'Trip Video Thumbnail is Required!'}
                  </Form.Text>
                )}
              />
            </Form.Group>
          )}

          <Form.Group controlId="tripVideoURLThumbnail">
            <Form.Label>Featured Images</Form.Label>
            <MediaSelectorField
              control={control}
              tripID={trip?.id}
              hostID={hostID}
              required
              name="featuredImages"
              selectButtonLabel="Select Featured Images"
              allowMultipleSelect
            />
            <ErrorMessage
              errors={errors}
              name="featuredImages"
              render={({ message }) => (
                <Form.Text as="p" className="text-danger">
                  {message ?? 'Featured Images are Required!'}
                </Form.Text>
              )}
            />
          </Form.Group>

          <Col xs={12} className="py-3">
            <strong>Meals</strong>

            <Form.Group controlId="meals.breakfast">
              <Form.Label>Number of days you will provide breakfast</Form.Label>
              <Form.Control type="number" {...register('meals.breakfast')} />
            </Form.Group>

            <Form.Group controlId="meals.breakfast">
              <Form.Label>Number of days you will provide lunch</Form.Label>
              <Form.Control type="number" {...register('meals.lunch')} />
            </Form.Group>

            <Form.Group controlId="meals.breakfast">
              <Form.Label>Number of days you will provide dinner</Form.Label>
              <Form.Control type="number" {...register('meals.dinner')} />
            </Form.Group>
          </Col>

          <Form.Group controlId="startLocation">
            <Form.Label>Start Location *</Form.Label>
            <GoogleMapsAutocompleteField
              name="address"
              control={control}
              setError={setError}
              clearErrors={clearErrors}
              required
            />
            <ErrorMessage
              errors={errors}
              name="address"
              render={({ message }) => (
                <Form.Text as="p" className="text-danger">
                  {message ?? 'Start location is required!'}
                </Form.Text>
              )}
            />
          </Form.Group>
          <div className="d-grid gap-2">
            <SubmitButton isSubmitting={isSubmitting || loading} block />
          </div>
        </Form>
      )}
    </>
  );
}
